import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, AlertTriangle, Clock3, CalendarDays, CheckCircle2, Paperclip, X,
  Search, Edit3, Download, Trash2, FileText, Users as UsersIcon,
  Wallet, CreditCard, Zap, Landmark, Repeat, Receipt, Building2,
  ChevronRight, Flame, Calendar, IndianRupee, Save, Loader2, MoreVertical, RotateCcw,
  MessageSquare, Send, Eye, ThumbsUp, RefreshCw,
} from "lucide-react";
import { useRealtime } from "@/hooks/useRealtime";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===================================================================
// Utilities — Indian number formatting + number-to-words
// ===================================================================
const ONES = ["", "One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
  "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
const TENS = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
const two = (n) => n < 20 ? ONES[n] : TENS[Math.floor(n/10)] + (n%10 ? "-" + ONES[n%10] : "");
const three = (n) => {
  if (!n) return "";
  const h = Math.floor(n/100), r = n%100;
  let s = "";
  if (h) s = ONES[h] + " Hundred" + (r ? " and " : "");
  if (r) s += two(r);
  return s;
};

export function amountInWords(amt) {
  if (amt === null || amt === undefined || amt === "" || isNaN(amt)) return "";
  const n = Number(amt);
  if (n < 0) return "Minus " + amountInWords(-n);
  let r = Math.floor(n), paise = Math.round((n - r) * 100);
  if (r === 0 && paise === 0) return "Zero Rupees Only";
  const parts = [];
  const cr = Math.floor(r/10000000); r %= 10000000;
  const lk = Math.floor(r/100000);   r %= 100000;
  const th = Math.floor(r/1000);     r %= 1000;
  if (cr) parts.push(two(cr) + " Crore");
  if (lk) parts.push(two(lk) + " Lakh");
  if (th) parts.push(two(th) + " Thousand");
  if (r)  parts.push(three(r));
  const rupees = parts.filter(Boolean).join(" ").trim();
  let out = rupees ? rupees + " Rupees" : "";
  if (paise) out = (out ? out + " and " : "") + two(paise) + " Paise";
  return (out + " Only").trim();
}

export function fmtInr(n, opts = {}) {
  if (n === null || n === undefined || n === "" || isNaN(n)) return "";
  const abs = Math.abs(Number(n));
  const s = abs.toFixed(2);
  const [i, d] = s.split(".");
  const last3 = i.slice(-3), rest = i.slice(0, -3);
  const withCommas = (rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," : "") + last3;
  const sign = Number(n) < 0 ? "-" : "";
  const hidePaise = opts.hidePaise && d === "00";
  return sign + "₹" + withCommas + (!hidePaise && d && d !== "00" ? "." + d : "");
}

const PAYMENT_METHODS = [
  { v: "UPI",            icon: Zap,       hint: "Instant" },
  { v: "Bank Transfer",  icon: Building2, hint: "Any channel" },
  { v: "NEFT",           icon: Landmark,  hint: "Net Electronic" },
  { v: "RTGS",           icon: Landmark,  hint: "Real-Time Gross" },
  { v: "IMPS",           icon: Zap,       hint: "24×7 Instant" },
  { v: "Cheque",         icon: Receipt,   hint: "Physical" },
  { v: "Cash",           icon: Wallet,    hint: "In-person" },
  { v: "Debit Card",     icon: CreditCard, hint: "Card" },
  { v: "Credit Card",    icon: CreditCard, hint: "Card" },
  { v: "Other",          icon: FileText,  hint: "Other" },
];

const PRIORITIES = [
  { v: "low",     label: "LOW",     cls: "bg-[#1a2320] text-[#8A8F98] border-[#3A4640]" },
  { v: "medium",  label: "MEDIUM",  cls: "bg-[#1f2b25] text-[#8EC2A9] border-[#3A4640]" },
  { v: "high",    label: "HIGH",    cls: "bg-[#3a2619] text-[#FFB46B] border-[#D97706]/40" },
  { v: "urgent",  label: "URGENT",  cls: "bg-[#3a1a1a] text-[#FF8080] border-[#DC2626]/50" },
];

// ===================================================================
// Shared tiny components
// ===================================================================
export function PriorityBadge({ value, size = "sm" }) {
  const p = PRIORITIES.find(x => x.v === value) || PRIORITIES[1];
  const padding = size === "lg" ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center gap-1 ${padding} rounded-md border font-heading uppercase tracking-wider ${p.cls}`}
          data-testid={`priority-badge-${value}`}>
      {value === "urgent" && <Flame size={10} />}
      {p.label}
    </span>
  );
}

function StatusDot({ status }) {
  const map = {
    overdue:   "bg-[#FF4A4A] shadow-[0_0_6px_rgba(255,74,74,0.6)]",
    due:       "bg-[#FFB46B] shadow-[0_0_6px_rgba(255,180,107,0.5)]",
    upcoming:  "bg-[#39FF14] shadow-[0_0_5px_rgba(57,255,20,0.4)]",
    completed: "bg-[#5A6872]",
    cancelled: "bg-[#5A6872]",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || "bg-[#5A6872]"}`} />;
}

function SummaryCard({ label, count, amount, icon: Icon, tone, active, onClick, testid }) {
  const tones = {
    red:    "border-[#DC2626]/40 hover:border-[#DC2626] bg-gradient-to-br from-[#2a0f0f] to-[#121815]",
    amber:  "border-[#D97706]/40 hover:border-[#D97706] bg-gradient-to-br from-[#2a1a0a] to-[#121815]",
    green:  "border-[#39FF14]/25 hover:border-[#39FF14] bg-gradient-to-br from-[#0f2214] to-[#121815]",
    muted:  "border-[#3A4640] hover:border-[#8EC2A9]/60 bg-gradient-to-br from-[#121815] to-[#121815]",
  };
  const iconColors = {
    red: "text-[#FF4A4A]", amber: "text-[#FFB46B]", green: "text-[#39FF14]", muted: "text-[#8A8F98]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border p-4 transition-all duration-200 ${tones[tone] || tones.muted}
                  ${active ? "ring-1 ring-[#39FF14]/60 border-[#39FF14]/70" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-heading uppercase tracking-[0.14em] text-[#8A8F98]">{label}</div>
        <Icon size={16} className={iconColors[tone] || iconColors.muted} />
      </div>
      <div className="mt-3 text-[26px] font-heading font-bold text-[#F8F9FA] leading-none tracking-tight">
        {fmtInr(amount, { hidePaise: true }) || "₹0"}
      </div>
      <div className="mt-1 text-xs text-[#8A8F98]">
        {count} {count === 1 ? "payment" : "payments"}
      </div>
    </button>
  );
}

function SectionCard({ title, icon: Icon, children, right, testid }) {
  return (
    <div className="rounded-xl border border-[#3A4640] bg-[#121815]" data-testid={testid}>
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={14} className="text-[#39FF14] shrink-0" />}
          <div className="text-[10px] font-heading uppercase tracking-[0.16em] text-[#8A8F98] truncate">
            {title}
          </div>
        </div>
        {right}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

function EmptyState({ icon: Icon = CheckCircle2, title, hint }) {
  return (
    <div className="rounded-xl border border-dashed border-[#3A4640] bg-[#121815]/40 px-6 py-8 text-center">
      <Icon size={28} className="mx-auto text-[#8A8F98] mb-2" />
      <div className="text-sm text-[#F8F9FA] font-medium">{title}</div>
      {hint && <div className="text-xs text-[#8A8F98] mt-1">{hint}</div>}
    </div>
  );
}

// ===================================================================
// Payee Typeahead — fixes the "stuck on Selected payee" bug
// ===================================================================
function PayeePicker({ value, name, onChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvedName, setResolvedName] = useState(name || "");
  const wrapRef = useRef(null);

  useEffect(() => { setResolvedName(name || ""); }, [name]);

  useEffect(() => {
    if (!value || resolvedName) return;
    axios.get(`${API}/accounts/payees`, { params: { q: "", limit: 1000 } })
      .then(r => {
        const found = (r.data.items || []).find(p => p.id === value);
        if (found) setResolvedName(found.name);
      }).catch(() => {});
  }, [value, resolvedName]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/accounts/payees`, { params: { q, limit: 10 } })
        .then(r => setItems(r.data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    function onClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function addNew() {
    const nm = q.trim();
    if (!nm) return;
    try {
      const r = await axios.post(`${API}/accounts/payees`, { name: nm });
      onChange(r.data.id, r.data.name);
      setResolvedName(r.data.name);
      setOpen(false);
      setQ("");
      toast.success(`Added "${r.data.name}" as new payee`);
    } catch (e) {
      if (e.response?.status === 409) toast.error("A payee with that name already exists — pick it from the list");
      else toast.error("Could not add payee");
    }
  }

  if (value && !open) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-[#3A4640] bg-[#0f1512] px-3 py-2.5"
           data-testid="payee-chip">
        <div className="flex items-center gap-2 min-w-0">
          <UsersIcon size={14} className="text-[#39FF14] shrink-0" />
          <div className="text-sm text-[#F8F9FA] font-medium truncate" data-testid="payee-chip-name">
            {resolvedName || "Selected payee"}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => { setQ(""); setItems([]); setOpen(true); }}
                  data-testid="payee-change-btn"
                  className="text-[11px] font-heading uppercase tracking-wider text-[#39FF14] hover:text-[#F8F9FA] px-2 py-1 rounded hover:bg-[#121815]">
            Change
          </button>
          <button type="button" onClick={() => { onChange(null, ""); setResolvedName(""); setQ(""); }}
                  data-testid="payee-clear-btn"
                  className="text-[#8A8F98] hover:text-[#FF4A4A] p-1 rounded" title="Remove payee">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  const exactMatch = items.some(p => p.name.toLowerCase() === q.trim().toLowerCase());
  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none" />
        <Input value={q} onFocus={() => setOpen(true)} onChange={e => { setQ(e.target.value); setOpen(true); }}
               placeholder="Search payee or type to create new…" className="pl-9"
               data-testid="payee-search" autoComplete="off" />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-[#0f1512] border border-[#3A4640] rounded-lg max-h-72 overflow-y-auto shadow-xl"
             data-testid="payee-dropdown">
          {loading && (
            <div className="px-3 py-2 text-xs text-[#8A8F98] flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Searching…
            </div>
          )}
          {!loading && items.length === 0 && !q.trim() && (
            <div className="px-3 py-3 text-xs text-[#8A8F98]">Start typing to search payees…</div>
          )}
          {items.map(p => (
            <button key={p.id} type="button"
                    onClick={() => { onChange(p.id, p.name); setResolvedName(p.name); setOpen(false); setQ(""); }}
                    data-testid={`payee-opt-${p.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#1a2320] text-sm flex items-center justify-between gap-2 border-b border-[#1a2320] last:border-b-0">
              <div className="flex items-center gap-2 min-w-0">
                <UsersIcon size={12} className="text-[#8A8F98] shrink-0" />
                <span className="text-[#F8F9FA] truncate">{p.name}</span>
                {p.short_name && <span className="text-[#8A8F98] text-xs">({p.short_name})</span>}
              </div>
              {p.usage_count > 0 && <span className="text-[10px] text-[#8A8F98] shrink-0">used {p.usage_count}×</span>}
            </button>
          ))}
          {q.trim() && !exactMatch && !loading && (
            <button type="button" onClick={addNew} data-testid="payee-add-new"
                    className="w-full text-left px-3 py-2.5 bg-[#0f1a12] hover:bg-[#133a1f] text-[#39FF14] text-sm font-medium border-t border-[#1a2320] flex items-center gap-2">
              <Plus size={14} /> Add "<span className="truncate max-w-[200px]">{q.trim()}</span>" as new payee
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Bank Picker
// ===================================================================
function BankPicker({ value, onChange, testid = "bank-select" }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/accounts/bank-accounts`).then(r => setItems(r.data.items || [])).catch(() => {});
  }, []);
  const current = items.find(b => b.id === value);
  return (
    <Select value={value || "__none"} onValueChange={v => onChange(v === "__none" ? null : v)}>
      <SelectTrigger data-testid={testid} className="bg-[#0f1512]">
        <SelectValue placeholder="— No preferred bank (optional) —">
          {current ? `${current.bank_name} · ${current.nickname} · ${current.account_number_masked}` : "— No preferred bank (optional) —"}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">— None —</SelectItem>
        {items.map(b => (
          <SelectItem key={b.id} value={b.id}>
            {b.bank_name} · {b.nickname} · <span className="font-mono">{b.account_number_masked}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ===================================================================
// Visual Amount Input
// ===================================================================
export function AmountInput({ value, onChange, placeholder = "0.00", large = false }) {
  const words = amountInWords(value);
  return (
    <div className="relative">
      <div className="relative">
        <IndianRupee size={large ? 20 : 16}
                     className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
        <input type="number" step="0.01" value={value ?? ""} placeholder={placeholder}
               onChange={e => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
               data-testid="amount-input"
               className={`w-full ${large ? "text-2xl py-3 pl-10 font-heading font-bold tracking-tight" : "text-base py-2 pl-9"} pr-3
                          bg-[#0f1512] border border-[#3A4640] rounded-lg text-[#F8F9FA]
                          focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14]/40 transition-colors`} />
      </div>
      {value !== null && value !== undefined && value !== "" && !isNaN(value) && (
        <div className="mt-1.5 text-xs text-[#8A8F98]" data-testid="amount-words">
          <span className="text-[#39FF14] font-semibold">{fmtInr(value)}</span>
          {words && <span className="ml-2">— {words}</span>}
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Payment Method card grid
// ===================================================================
function PaymentMethodGrid({ value, onChange, testidPrefix = "pay-method" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
      {PAYMENT_METHODS.map(m => {
        const Icon = m.icon;
        const active = value === m.v;
        const safeKey = m.v.replace(/\s/g, "");
        return (
          <button key={m.v} type="button" onClick={() => onChange(active ? null : m.v)}
                  data-testid={`${testidPrefix}-${safeKey}`}
                  className={`group flex flex-col items-center gap-1 rounded-lg border py-2.5 px-2 text-center transition-all
                              ${active
                                ? "border-[#39FF14] bg-[#0f1a12] text-[#39FF14] shadow-[inset_0_0_0_1px_rgba(57,255,20,0.25)]"
                                : "border-[#3A4640] bg-[#0f1512] text-[#F8F9FA] hover:border-[#8EC2A9]/50"}`}>
            <Icon size={16} className={active ? "text-[#39FF14]" : "text-[#8A8F98] group-hover:text-[#F8F9FA]"} />
            <div className="text-[11px] font-medium">{m.v}</div>
          </button>
        );
      })}
    </div>
  );
}

// ===================================================================
// Priority selector
// ===================================================================
function PrioritySelector({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="priority-selector">
      {PRIORITIES.map(p => {
        const active = value === p.v;
        return (
          <button key={p.v} type="button" onClick={() => onChange(p.v)}
                  data-testid={`priority-opt-${p.v}`}
                  className={`px-3 py-1.5 rounded-md border text-[11px] font-heading uppercase tracking-wider transition-all
                              ${active ? "ring-1 ring-[#39FF14]/60 scale-[1.02] " + p.cls : p.cls + " opacity-70 hover:opacity-100"}`}>
            {p.v === "urgent" && <Flame size={10} className="inline mr-1 -mt-0.5" />}
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// ===================================================================
// Date + Time
// ===================================================================
function DueDateTime({ date: d, time: t, onChange }) {
  const [noTime, setNoTime] = useState(!t);
  useEffect(() => { if (noTime && t) onChange({ date: d, time: null }); }, [noTime]); // eslint-disable-line

  function setPreset(kind) {
    const today = new Date();
    const iso = (dt) => dt.toISOString().slice(0, 10);
    if (kind === "today") onChange({ date: iso(today), time: t });
    else if (kind === "tomorrow") { const x = new Date(today); x.setDate(x.getDate() + 1); onChange({ date: iso(x), time: t }); }
    else if (kind === "week") { const x = new Date(today); x.setDate(x.getDate() + 7); onChange({ date: iso(x), time: t }); }
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">Due date</label>
          <div className="relative">
            <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none" />
            <Input type="date" value={d || ""}
                   onChange={e => onChange({ date: e.target.value, time: t })}
                   className="pl-9 bg-[#0f1512]" data-testid="rem-due" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">Due time</label>
          <div className="relative">
            <Clock3 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none" />
            <Input type="time" disabled={noTime} value={noTime ? "" : (t || "")}
                   onChange={e => onChange({ date: d, time: e.target.value || null })}
                   className="pl-9 bg-[#0f1512] disabled:opacity-50" data-testid="rem-time" />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setPreset("today")}
                className="text-[11px] px-2 py-1 rounded border border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#8EC2A9]/60"
                data-testid="date-preset-today">Today</button>
        <button type="button" onClick={() => setPreset("tomorrow")}
                className="text-[11px] px-2 py-1 rounded border border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#8EC2A9]/60"
                data-testid="date-preset-tomorrow">Tomorrow</button>
        <button type="button" onClick={() => setPreset("week")}
                className="text-[11px] px-2 py-1 rounded border border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#8EC2A9]/60"
                data-testid="date-preset-week">+ 1 week</button>
        <label className="ml-auto flex items-center gap-2 text-xs text-[#8A8F98] cursor-pointer">
          <input type="checkbox" checked={noTime} onChange={e => setNoTime(e.target.checked)} data-testid="rem-no-time" />
          No specific time
        </label>
      </div>
    </div>
  );
}

// ===================================================================
// Attachment Panel
// ===================================================================
function AttachmentPanel({ staged, setStaged, uploaded, onDelete, onDownload }) {
  const inpRef = useRef(null);
  function pick(files) {
    const arr = Array.from(files || []);
    for (const f of arr) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" exceeds 10 MB`); continue; }
      setStaged(prev => [...prev, f]);
    }
  }
  return (
    <div onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
         onDrop={e => { e.preventDefault(); e.stopPropagation(); pick(e.dataTransfer.files); }}
         className="rounded-lg border border-dashed border-[#3A4640] bg-[#0f1512] p-4"
         data-testid="attach-dropzone">
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={inpRef} type="file" multiple className="hidden"
               onChange={e => pick(e.target.files)} data-testid="attach-file-input" />
        <Button type="button" variant="outline" onClick={() => inpRef.current?.click()}
                data-testid="attach-add-btn"
                className="border-[#3A4640] bg-[#121815] text-[#F8F9FA] hover:border-[#39FF14] hover:text-[#39FF14]">
          <Paperclip size={14} className="mr-2" /> Add documents
        </Button>
        <div className="text-xs text-[#8A8F98]">Drag &amp; drop here • up to 10 MB each</div>
      </div>
      {(staged?.length > 0 || uploaded?.length > 0) && (
        <div className="mt-3 space-y-1.5" data-testid="attach-list">
          {uploaded?.map(a => (
            <div key={a.id} className="flex items-center gap-2 bg-[#121815] border border-[#3A4640] rounded-md px-3 py-2"
                 data-testid={`attach-row-${a.id}`}>
              <FileText size={14} className="text-[#8EC2A9]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate text-[#F8F9FA]">{a.filename}</div>
                <div className="text-[10px] text-[#8A8F98]">{Math.round((a.size || 0) / 1024)} KB</div>
              </div>
              <button type="button" onClick={() => onDownload?.(a)}
                      className="text-[#8A8F98] hover:text-[#39FF14] p-1" title="Download"><Download size={14} /></button>
              <button type="button" onClick={() => onDelete?.(a)}
                      className="text-[#8A8F98] hover:text-[#FF4A4A] p-1" title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
          {staged?.map((f, i) => (
            <div key={`st-${i}`} className="flex items-center gap-2 bg-[#121815] border border-dashed border-[#3A4640] rounded-md px-3 py-2"
                 data-testid={`staged-row-${i}`}>
              <FileText size={14} className="text-[#FFB46B]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate text-[#F8F9FA]">{f.name}</div>
                <div className="text-[10px] text-[#FFB46B]">Will upload after save • {Math.round(f.size / 1024)} KB</div>
              </div>
              <button type="button" onClick={() => setStaged(prev => prev.filter((_, j) => j !== i))}
                      className="text-[#8A8F98] hover:text-[#FF4A4A] p-1"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Reminder Form
// ===================================================================
function ReminderForm({ onDone, isAccountant, users }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState({ date: "", time: "" });
  const [payeeId, setPayeeId] = useState(null);
  const [payeeName, setPayeeName] = useState("");
  const [amount, setAmount] = useState(null);
  const [priority, setPriority] = useState("medium");
  const [prefMethod, setPrefMethod] = useState(null);
  const [prefBank, setPrefBank] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState(null);
  const [staged, setStaged] = useState([]);
  const [recurring, setRecurring] = useState(false);
  const [dayOfMonth, setDayOfMonth] = useState(10);
  const [recurEnd, setRecurEnd] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!due.date)     { toast.error("Due date is required"); return; }
    // At least one of description, payee, amount or instructions must be present —
    // mirrors backend validation so the user gets a clear message locally first.
    const hasContent = title.trim() || payeeId || (amount !== null && amount !== "") || instructions.trim() || notes.trim();
    if (!hasContent) {
      toast.error("Add at least a description, payee, amount or instructions");
      return;
    }
    const body = {
      title: title.trim(),
      due_date: due.date,
      due_time: due.time || null,
      payee_id: payeeId,
      amount,
      priority,
      preferred_payment_method: prefMethod,
      preferred_bank_account_id: prefBank,
      instructions: instructions.trim() || null,
      notes: notes.trim() || null,
      assigned_to_user_id: assignedTo,
    };
    if (recurring) {
      body.recurrence = {
        frequency: "monthly",
        day_of_month: parseInt(dayOfMonth, 10) || 1,
        start_date: due.date,
        end_date: recurEnd || null,
        active: true,
      };
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/accounts/reminders`, body);
      const rid = r.data.id;
      for (const f of staged) {
        const fd = new FormData(); fd.append("file", f);
        try {
          await axios.post(`${API}/accounts/attachments/reminder/${rid}`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.error(`Could not upload ${f.name}`); }
      }
      toast.success("Payment reminder created");
      try { await axios.post(`${API}/accounts/_ping-created/reminder/${rid}`); } catch { /* best-effort */ }
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create reminder");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="reminder-form">
      {/* Live summary */}
      <div className="rounded-xl border border-[#39FF14]/25 bg-gradient-to-br from-[#0f2214] to-[#121815] p-4"
           data-testid="rem-live-summary">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-heading uppercase tracking-[0.16em] text-[#8A8F98]">Live summary</div>
            <div className="text-base font-heading font-bold text-[#F8F9FA] truncate mt-0.5" data-testid="live-title">
              💳 {title.trim() || (payeeName && amount ? `Payment to ${payeeName} — ${fmtInr(amount, { hidePaise: true })}` : payeeName ? `Payment to ${payeeName}` : "New payment reminder")}
            </div>
            {amount !== null && amount !== "" && !isNaN(amount) ? (
              <>
                <div className="mt-2 text-2xl font-heading font-bold text-[#39FF14] tracking-tight">{fmtInr(amount)}</div>
                <div className="text-xs text-[#8A8F98] mt-0.5">{amountInWords(amount)}</div>
              </>
            ) : (
              <div className="mt-2 text-sm text-[#8A8F98]">Amount not entered yet</div>
            )}
          </div>
          <PriorityBadge value={priority} size="lg" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#8A8F98]">
          <span className="flex items-center gap-1.5"><UsersIcon size={12} />{payeeName || "No payee selected"}</span>
          <span className="flex items-center gap-1.5"><Calendar size={12} />{due.date || "No date"}</span>
          <span className="flex items-center gap-1.5"><Clock3 size={12} />{due.time || "No specific time"}</span>
        </div>
      </div>

      <SectionCard title="Who are we paying?" icon={UsersIcon} testid="sec-payee">
        <PayeePicker value={payeeId} name={payeeName}
                     onChange={(id, nm) => { setPayeeId(id); setPayeeName(nm || ""); }} />
      </SectionCard>

      <SectionCard title="Description / Reference (optional)" icon={Receipt} testid="sec-amount">
        <div className="space-y-3">
          <Input placeholder='e.g. "Power Weeder — October invoice" (optional)'
                 value={title} onChange={e => setTitle(e.target.value)} data-testid="rem-title" />
          <AmountInput value={amount} onChange={setAmount} large />
        </div>
      </SectionCard>

      <SectionCard title="When is it due?" icon={CalendarDays} testid="sec-when">
        <DueDateTime date={due.date} time={due.time} onChange={setDue} />
      </SectionCard>

      <SectionCard title="Payment preference" icon={CreditCard} testid="sec-method"
                   right={<span className="text-[10px] text-[#8A8F98] italic hidden sm:inline">Preference only — actual method is captured at payment time</span>}>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-2">Preferred method</label>
            <PaymentMethodGrid value={prefMethod} onChange={setPrefMethod} testidPrefix="rem-method" />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-2">Preferred bank account (optional)</label>
            <BankPicker value={prefBank} onChange={setPrefBank} testid="rem-bank" />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Priority & assignment" icon={Flame} testid="sec-priority">
        <div className="space-y-3">
          <PrioritySelector value={priority} onChange={setPriority} />
          {!isAccountant && users?.length > 0 && (
            <div>
              <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">Assigned to</label>
              <Select value={assignedTo || "__self"}
                      onValueChange={v => setAssignedTo(v === "__self" ? null : v)}>
                <SelectTrigger data-testid="rem-assign" className="bg-[#0f1512]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self">Me</SelectItem>
                  {users.filter(u => u.role === "accountant").map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email} (Accountant)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Documents" icon={Paperclip} testid="sec-attach">
        <AttachmentPanel staged={staged} setStaged={setStaged} />
      </SectionCard>

      <SectionCard title="Instructions for accountant" icon={Edit3} testid="sec-instructions">
        <Textarea placeholder="e.g. Pay before 11 AM. Collect GST invoice and upload payment confirmation."
                  value={instructions} onChange={e => setInstructions(e.target.value)}
                  data-testid="rem-instructions" className="min-h-[90px] bg-[#0f1512]" />
      </SectionCard>

      <SectionCard title="Internal notes" icon={FileText} testid="sec-notes">
        <Textarea placeholder="Internal notes (optional)"
                  value={notes} onChange={e => setNotes(e.target.value)}
                  data-testid="rem-notes" className="min-h-[60px] bg-[#0f1512]" />
      </SectionCard>

      <SectionCard title="Recurrence" icon={Repeat} testid="sec-recur">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setRecurring(false)} data-testid="recur-onetime"
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all
                                ${!recurring ? "border-[#39FF14] bg-[#0f1a12] text-[#39FF14]" : "border-[#3A4640] bg-[#0f1512] text-[#8A8F98]"}`}>
              One-time payment
            </button>
            <button type="button" onClick={() => setRecurring(true)} data-testid="recur-monthly"
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all
                                ${recurring ? "border-[#39FF14] bg-[#0f1a12] text-[#39FF14]" : "border-[#3A4640] bg-[#0f1512] text-[#8A8F98]"}`}>
              Repeat every month
            </button>
          </div>
          {recurring && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">Pay on day</label>
                <Input type="number" min={1} max={31} value={dayOfMonth}
                       onChange={e => setDayOfMonth(e.target.value)}
                       className="bg-[#0f1512]" data-testid="recur-day" />
              </div>
              <div>
                <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">End date (optional)</label>
                <Input type="date" value={recurEnd} onChange={e => setRecurEnd(e.target.value)}
                       className="bg-[#0f1512]" data-testid="recur-end" />
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-[#0B0F0C]/95 backdrop-blur border-t border-[#3A4640] flex items-center justify-end gap-2">
        <Button onClick={submit} disabled={saving} data-testid="rem-create-submit"
                className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
          Create Reminder
        </Button>
      </div>
    </div>
  );
}

// ===================================================================
// Confirm Completion dialog — explicit confirmation step
// ===================================================================
function ConfirmCompleteDialog({ open, reminder, onCancel, onConfirm }) {
  const [saving, setSaving] = useState(false);
  if (!reminder) return null;
  async function go() {
    if (saving) return; // double-click guard
    setSaving(true);
    try { await onConfirm(reminder); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md bg-[#0B0F0C] border-[#3A4640]" data-testid="confirm-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CheckCircle2 size={16} className="text-[#39FF14]" /> Confirm Payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#F8F9FA]">Are you sure you want to mark this payment as completed?</p>
          <div className="rounded-lg border border-[#3A4640] bg-[#121815] p-3 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-[#8A8F98]">Payee</span>
              <span className="text-[#F8F9FA]">{reminder._payeeName || "—"}</span></div>
            <div className="flex justify-between"><span className="text-[#8A8F98]">Amount</span>
              <span className="text-[#F8F9FA] font-semibold">{reminder.amount ? fmtInr(reminder.amount) : "—"}</span></div>
            {reminder.preferred_payment_method && (
              <div className="flex justify-between"><span className="text-[#8A8F98]">Preferred method</span>
                <span className="text-[#F8F9FA]">{reminder.preferred_payment_method}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[#8A8F98]">Due</span>
              <span className="text-[#F8F9FA]">{reminder.due_date}{reminder.due_time ? ` · ${reminder.due_time}` : ""}</span></div>
          </div>
          <div className="rounded-md border border-[#FFB46B]/30 bg-[#2a1a0a]/40 p-2 text-xs text-[#FFB46B] flex gap-2">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            This marks the reminder as completed and removes it from active queues.
            Owner/Admin can reopen it later if needed.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} data-testid="confirm-complete-cancel"
                  className="border-[#3A4640] bg-transparent text-[#F8F9FA] hover:border-[#8EC2A9]/50">
            Cancel
          </Button>
          <Button onClick={go} disabled={saving} data-testid="confirm-complete-ok"
                  className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
            Confirm Payment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===================================================================
// Reopen dialog — Owner / Admin only
// ===================================================================
function ReopenDialog({ open, reminder, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setReason(""); } }, [open]);
  if (!reminder) return null;
  // Compute what the status will become after reopen
  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  let newStatus = "upcoming";
  if (reminder.due_date < todayIso) newStatus = "overdue";
  else if (reminder.due_date === todayIso) newStatus = "due today";
  async function go() {
    if (saving) return;
    setSaving(true);
    try { await onConfirm(reminder, reason.trim()); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md bg-[#0B0F0C] border-[#3A4640]" data-testid="confirm-reopen-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <RotateCcw size={16} className="text-[#FFB46B]" /> Reopen Payment?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#F8F9FA]">
            This payment is currently marked as <b>completed</b>. Reopening will
            return the reminder to an active state based on its due date/time.
          </p>
          <div className="rounded-lg border border-[#3A4640] bg-[#121815] p-3 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-[#8A8F98]">Current status</span>
              <span className="text-[#F8F9FA]">Completed</span></div>
            <div className="flex justify-between"><span className="text-[#8A8F98]">Due</span>
              <span className="text-[#F8F9FA]">{reminder.due_date}{reminder.due_time ? ` · ${reminder.due_time}` : ""}</span></div>
            <div className="flex justify-between"><span className="text-[#8A8F98]">After reopen</span>
              <span className="text-[#39FF14] uppercase font-heading text-xs">{newStatus}</span></div>
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] mb-1">
              Reason (optional)
            </label>
            <Textarea placeholder="e.g. Marked completed by mistake."
                      value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="reopen-reason" className="bg-[#0f1512] min-h-[70px]" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} data-testid="reopen-cancel"
                  className="border-[#3A4640] bg-transparent text-[#F8F9FA] hover:border-[#8EC2A9]/50">
            Cancel
          </Button>
          <Button onClick={go} disabled={saving} data-testid="reopen-ok"
                  className="bg-[#FFB46B] text-[#0B0F0C] hover:bg-[#FFB46B]/90 font-heading font-semibold">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RotateCcw size={14} className="mr-2" />}
            Reopen Payment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===================================================================
// Shared Chat panel — used by the reminder chat drawer AND the
// standalone Global Owner↔Accountant chat page.
// Props:
//   scope     — "reminder" | "global"
//   scopeId   — reminder id or "main"
//   subtitle  — small line under the title
//   className — extra classes on the outer div
// Server-side permission is re-checked at every chat endpoint; we also
// short-circuit on 403 by surfacing a toast and closing (via onForbidden).
// ===================================================================
export function ChatPanel({ scope, scopeId, subtitle, onForbidden, autoScroll = true, heightClass = "h-[55vh]" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [staged, setStaged] = useState(null); // single file at a time (keeps UX simple)
  const [uploading, setUploading] = useState(false);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const canUse = ["admin", "co_admin", "owner", "accountant"].includes(user?.role);

  async function load() {
    if (!scopeId) return;
    try {
      const r = await axios.get(`${API}/accounts/chat/${scope}/${scopeId}/messages`);
      setMessages(r.data?.items || []);
      axios.post(`${API}/accounts/chat/${scope}/${scopeId}/seen`).catch(() => {});
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Not authorized for this chat");
        onForbidden?.();
      }
    }
  }

  useEffect(() => { if (scopeId) load(); }, [scope, scopeId]); // eslint-disable-line

  useRealtime((ev) => {
    if (ev.type === "chat" && ev.scope === `${scope}:${scopeId}`) load();
  }, { enabled: !!scopeId });

  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);

  function onFilePicked(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes((f.type || "").toLowerCase())) {
      toast.error("Only PDF, JPG or PNG files are allowed");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit");
      e.target.value = "";
      return;
    }
    setStaged(f);
    e.target.value = "";
  }

  async function send() {
    const t = text.trim();
    if ((!t && !staged) || sending || uploading) return;
    if (staged) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", staged);
        if (t) fd.append("text", t);
        await axios.post(
          `${API}/accounts/chat/${scope}/${scopeId}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setText("");
        setStaged(null);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not send attachment");
      } finally {
        setUploading(false);
      }
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/accounts/chat/${scope}/${scopeId}/messages`, { text: t });
      setText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  }

  if (!canUse) {
    return (
      <div className="p-6 text-center text-sm text-[#8A8F98]" data-testid={`chat-forbidden-${scope}`}>
        You don't have access to this chat.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid={`chat-panel-${scope}`}>
      {subtitle && (
        <div className="text-[11px] text-[#8A8F98] px-5 pt-2 pb-1">{subtitle}</div>
      )}
      <div ref={listRef} className={`px-5 py-3 space-y-2 overflow-y-auto flex-1 ${heightClass}`}
           data-testid={`chat-list-${scope}`}>
        {messages.length === 0 && (
          <div className="text-center text-xs text-[#8A8F98] py-8">
            No messages yet. Start the conversation below.
          </div>
        )}
        {messages.map((m, idx) => {
          const isSelf = m.sender_id === user?.id;
          const seenByOther = (m.seen_by || []).some(uid => uid !== m.sender_id);
          const att = m.attachment;
          const prev = idx > 0 ? messages[idx - 1] : null;
          // Group consecutive messages from the same sender — hide repeated name
          const sameAsPrev = prev && prev.sender_id === m.sender_id &&
            (new Date(m.created_at) - new Date(prev.created_at)) < 3 * 60_000;
          const roleLabel = m.sender_role === "accountant" ? "Accountant"
            : m.sender_role === "owner" ? "Owner"
            : (m.sender_role === "admin" || m.sender_role === "co_admin") ? "Admin"
            : "";
          // Real human display name — never role, never raw email
          const displayName = (m.sender_name && String(m.sender_name).trim())
            || (m.sender_email ? m.sender_email.split("@")[0] : "")
            || "User";
          const initials = displayName.split(/[\s.]+/).filter(Boolean)
            .slice(0, 2).map(s => s[0]?.toUpperCase()).join("") || "U";
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`}
                 className={`flex gap-2 ${isSelf ? "justify-end" : "justify-start"} ${sameAsPrev ? "-mt-1" : "mt-2"}`}>
              {/* Avatar on the OTHER side only, aligned to bubble start */}
              {!isSelf && (
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#1A1F1C] border border-[#F59E0B]/40 text-[#F59E0B] text-[10px] font-heading font-bold flex items-center justify-center self-end mb-1"
                     title={displayName}
                     style={{ visibility: sameAsPrev ? "hidden" : "visible" }}>
                  {initials}
                </div>
              )}
              <div className={`flex flex-col max-w-[72%] ${isSelf ? "items-end" : "items-start"}`}>
                {/* Sender header — real name + role chip; hidden when grouped */}
                {!sameAsPrev && (
                  <div className={`flex items-center gap-1.5 mb-1 px-0.5 text-[11px] ${
                      isSelf ? "flex-row-reverse" : ""}`}>
                    <span className={`font-heading font-semibold ${
                        isSelf ? "text-[#39FF14]" : "text-[#F59E0B]"}`}
                          data-testid={`chat-msg-name-${m.id}`}>
                      {isSelf ? "You" : displayName}
                    </span>
                    {roleLabel && (
                      <span className="text-[9px] uppercase tracking-wider text-[#8A8F98] px-1 py-[1px] rounded border border-[#3A4640]">
                        {roleLabel}
                      </span>
                    )}
                  </div>
                )}
                {/* Bubble — distinct per side */}
                <div className={`rounded-2xl px-3.5 py-2 ${
                    isSelf
                      ? "bg-[#1a3524] border border-[#39FF14]/35 text-[#F8F9FA] rounded-tr-sm shadow-[0_2px_8px_rgba(57,255,20,0.08)]"
                      : "bg-[#1A1F1C] border border-[#3A4640] text-[#F8F9FA] rounded-tl-sm shadow-[0_2px_8px_rgba(245,158,11,0.05)]"
                  }`}>
                  {att && <ChatAttachment att={att} />}
                  {m.text && (
                    <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                  )}
                </div>
                {/* Timestamp + read receipt */}
                <div className={`text-[10px] mt-0.5 px-0.5 flex items-center gap-1 ${
                    isSelf ? "text-[#8EC2A9]" : "text-[#8A8F98]"}`}>
                  <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  {isSelf && (seenByOther
                    ? <Eye size={10} className="text-[#39FF14]" title="Seen" />
                    : <span title="Sent">✓</span>)}
                </div>
              </div>
              {/* Avatar on self side */}
              {isSelf && (
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#1a3524] border border-[#39FF14]/50 text-[#39FF14] text-[10px] font-heading font-bold flex items-center justify-center self-end mb-1"
                     title={displayName}
                     style={{ visibility: sameAsPrev ? "hidden" : "visible" }}>
                  {initials}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {staged && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-[#3A4640] bg-[#0f1512] px-3 py-2"
             data-testid={`chat-staged-${scope}`}>
          <Paperclip size={14} className="text-[#39FF14] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-[#F8F9FA] truncate">{staged.name}</div>
            <div className="text-[10px] text-[#8A8F98]">{(staged.size / 1024).toFixed(0)} KB · {staged.type}</div>
          </div>
          <Button size="sm" variant="ghost"
                  className="h-7 w-7 px-0 text-[#8A8F98] hover:text-red-400"
                  onClick={() => setStaged(null)}
                  data-testid={`chat-staged-remove-${scope}`}>
            <X size={14} />
          </Button>
        </div>
      )}
      <div className="border-t border-[#3A4640] px-4 py-3 flex items-center gap-2 bg-[#121815]">
        <input ref={fileInputRef} type="file" className="hidden"
               accept="application/pdf,image/jpeg,image/png"
               onChange={onFilePicked}
               data-testid={`chat-file-input-${scope}`} />
        <Button size="sm" variant="ghost"
                className="h-9 w-9 px-0 text-[#8A8F98] hover:text-[#39FF14]"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!staged || uploading || sending}
                title="Attach PDF / JPG / PNG (10 MB max)"
                data-testid={`chat-attach-${scope}`}>
          <Paperclip size={16} />
        </Button>
        <Input placeholder={staged ? "Add a caption (optional)…" : "Write a message…"}
               value={text}
               onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
               data-testid={`chat-input-${scope}`} className="bg-[#0f1512]" />
        <Button onClick={send} disabled={sending || uploading || (!text.trim() && !staged)}
                data-testid={`chat-send-${scope}`}
                className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
          {(sending || uploading) ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </div>
    </div>
  );
}

// Attachment bubble inside a chat message
function ChatAttachment({ att }) {
  const url = `${API}/accounts/chat/attachments/${att.id}/download`;
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (att.kind !== "image" || !token) return;
    let revoked = null;
    axios.get(url, { responseType: "blob" })
      .then((r) => {
        const u = URL.createObjectURL(r.data);
        revoked = u;
        setImgSrc(u);
      })
      .catch(() => {});
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [att.id, att.kind]); // eslint-disable-line

  async function openFile() {
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank", "noopener");
      // revoke later; browser needs the URL alive until the tab opens
      setTimeout(() => URL.revokeObjectURL(u), 60_000);
    } catch { toast.error("Could not open attachment"); }
  }

  if (att.kind === "image") {
    return (
      <button type="button" onClick={openFile}
              className="block mb-1 rounded-lg overflow-hidden border border-[#3A4640] hover:border-[#39FF14]/60"
              data-testid={`chat-att-img-${att.id}`}>
        {imgSrc
          ? <img src={imgSrc} alt={att.filename}
                 className="max-w-[260px] max-h-[260px] object-cover" />
          : <div className="w-[200px] h-[120px] flex items-center justify-center bg-[#0f1512] text-[10px] text-[#8A8F98]">
              Loading…
            </div>}
        <div className="px-2 py-1 text-[10px] text-[#8EC2A9] bg-[#0f1512] text-left">
          {att.filename}
        </div>
      </button>
    );
  }
  // PDF (or other) → stylised card with icon
  return (
    <button type="button" onClick={openFile}
            className="flex items-center gap-2 mb-1 rounded-lg border border-[#3A4640] bg-[#0f1512] px-3 py-2 hover:border-[#39FF14]/60"
            data-testid={`chat-att-pdf-${att.id}`}>
      <FileText size={18} className="text-[#39FF14] shrink-0" />
      <div className="min-w-0 text-left">
        <div className="text-xs text-[#F8F9FA] truncate max-w-[200px]">{att.filename}</div>
        <div className="text-[10px] text-[#8A8F98]">
          {Math.ceil((att.size || 0) / 1024)} KB · {att.content_type}
        </div>
      </div>
      <Download size={14} className="text-[#8A8F98]" />
    </button>
  );
}

// ===================================================================
// Reminder Chat Drawer — payment-specific conversation
// ===================================================================
function ReminderChatDrawer({ open, reminder, onClose }) {
  const scopeId = reminder?.id;
  if (!reminder) return null;
  const titleText = reminder.display_title || reminder.title || "Payment reminder";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B0F0C] border-[#3A4640] p-0 overflow-hidden"
                     data-testid="rem-chat-drawer">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#3A4640]">
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageSquare size={16} className="text-[#39FF14]" />
            Chat · <span className="text-sm text-[#8EC2A9] font-normal truncate">{titleText}</span>
          </DialogTitle>
          <div className="text-[11px] text-[#8A8F98]">
            Messages are strictly scoped to this reminder and never change payment status.
          </div>
        </DialogHeader>
        <ChatPanel scope="reminder" scopeId={scopeId}
                   onForbidden={onClose}
                   heightClass="h-[55vh]" />
      </DialogContent>
    </Dialog>
  );
}

// ===================================================================
// Reminder card for main screen
// ===================================================================
function ReminderCard({ r, onComplete, onPay, onReopen, canReopen, onAck, onChat, canAck }) {
  const dueLabel = r.due_time ? `${r.due_date} • ${r.due_time}` : r.due_date;
  const titleText = r.display_title || r.title || "Payment reminder";
  const isDone = r.status === "completed" || r.status === "cancelled";
  const isAcked = !!r.acknowledged_at;
  return (
    <div className="rounded-xl border border-[#3A4640] bg-[#121815] hover:border-[#8EC2A9]/50 transition-colors p-4 flex flex-col gap-2"
         data-testid={`rem-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot status={r.status} />
          <div className="text-sm font-medium text-[#F8F9FA] truncate" data-testid={`rem-card-title-${r.id}`}>
            {titleText}
          </div>
          {r.is_recurring_occurrence && (
            <Badge variant="secondary" className="shrink-0 text-[9px]"><Repeat size={9} className="mr-1" /> MONTHLY</Badge>
          )}
          {isDone && (
            <Badge className="shrink-0 text-[9px] bg-[#1a2320] text-[#8EC2A9] border-[#3A4640] border">✓ COMPLETED</Badge>
          )}
          {!isDone && isAcked && (
            <Badge className="shrink-0 text-[9px] bg-[#0f2214] text-[#39FF14] border-[#39FF14]/40 border"
                   data-testid={`rem-card-ack-${r.id}`}>
              👁 ACK
            </Badge>
          )}
        </div>
        {!isDone && <PriorityBadge value={r.priority} />}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xl font-heading font-bold text-[#F8F9FA] tracking-tight">
            {r.amount ? fmtInr(r.amount, { hidePaise: true }) : <span className="text-[#8A8F98]">—</span>}
          </div>
          <div className="text-[11px] text-[#8A8F98] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1"><CalendarDays size={11} />{dueLabel}</span>
            {r.preferred_payment_method && (
              <span className="flex items-center gap-1 text-[#8EC2A9]"><CreditCard size={11} />{r.preferred_payment_method}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onChat && (
            <Button size="sm" variant="ghost" className="h-8 w-8 px-0 text-[#8A8F98] hover:text-[#39FF14]"
                    onClick={() => onChat(r)} data-testid={`rem-chat-${r.id}`} title="Chat">
              <MessageSquare size={14} />
            </Button>
          )}
          {!isDone && canAck && !isAcked && onAck && (
            <Button size="sm" variant="outline"
                    className="h-8 px-2 border-[#3A4640] text-[#8EC2A9] hover:border-[#39FF14] hover:text-[#39FF14]"
                    onClick={() => onAck(r)} data-testid={`rem-ack-${r.id}`}>
              <ThumbsUp size={12} className="mr-1" /> Ack
            </Button>
          )}
          {!isDone && onPay && (
            <Button size="sm" variant="outline"
                    className="h-8 px-2 border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14] hover:text-[#39FF14]"
                    onClick={() => onPay(r)} data-testid={`rem-pay-${r.id}`}>Pay</Button>
          )}
          {!isDone && onComplete && (
            <Button size="sm" variant="ghost" className="h-8 px-2 text-[#39FF14] hover:text-[#F8F9FA]"
                    onClick={() => onComplete(r)} data-testid={`rem-complete-${r.id}`}>
              <CheckCircle2 size={14} />
            </Button>
          )}
          {isDone && canReopen && onReopen && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 px-0 text-[#8A8F98] hover:text-[#F8F9FA]"
                        data-testid={`rem-more-${r.id}`}>
                  <MoreVertical size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121815] border-[#3A4640]">
                <DropdownMenuItem onClick={() => onReopen(r)} data-testid={`rem-reopen-${r.id}`}
                                  className="text-[#FFB46B] focus:text-[#FFB46B] focus:bg-[#2a1a0a]/40 cursor-pointer">
                  <RotateCcw size={12} className="mr-2" /> Reopen Payment
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// MAIN SCREEN
// ===================================================================
export function AccountsReminders() {
  const { user } = useAuth();
  const isAccountant = user?.role === "accountant";
  const canReopen = ["admin", "co_admin", "owner"].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("attention");
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [payOpen, setPayOpen] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(null);
  const [reopenOf, setReopenOf] = useState(null);
  const [chatOf, setChatOf] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now()); // forces re-render for "x ago"
  const canAck = isAccountant || ["admin", "co_admin", "owner"].includes(user?.role);

  // Dedup guard — the moment a reload STARTS we stamp this ref. Both the
  // manual Refresh click and the 10-minute reconciliation interval respect
  // this stamp. SSE events are allowed through immediately (they're the
  // primary signal) but if they fire a reload we also update the stamp, so
  // a manual click 300ms later is a no-op instead of a duplicate request.
  const lastReloadStartedRef = useRef(0);
  const inflightRef = useRef(false);

  async function reload({ reason = "manual" } = {}) {
    const now = Date.now();
    // Hard dedup: if another reload is already in flight, drop this one.
    if (inflightRef.current) return;
    // Soft dedup: an SSE-triggered or manual reload <1.5 s ago already
    // fetched the latest state — skip.
    if (now - lastReloadStartedRef.current < 1500) return;
    lastReloadStartedRef.current = now;
    inflightRef.current = true;
    if (reason === "manual") setRefreshing(true);
    try {
      axios.get(`${API}/accounts/_tick`).catch(() => {});
      const [s, r] = await Promise.all([
        axios.get(`${API}/accounts/reminders/summary`),
        axios.get(`${API}/accounts/reminders`, { params: { include_cancelled: false, limit: 300 } }),
      ]);
      setSummary(s.data);
      setItems(r.data.items || []);
      setLastUpdated(Date.now());
    } catch {
      if (reason === "manual") toast.error("Could not refresh Payment Desk");
    } finally {
      inflightRef.current = false;
      if (reason === "manual") setRefreshing(false);
    }
  }

  // Live refresh when backend pushes a reminder/payment event (SSE is primary).
  useRealtime((ev) => {
    if (ev.type === "reminder" || ev.type === "payment") reload({ reason: "sse" });
  }, { enabled: true });

  // Initial load + 10-minute reconciliation fallback. Guarantees eventual
  // consistency if SSE temporarily disconnects. If any reload (SSE OR manual)
  // happened within the last 5 minutes, we SKIP the tick — the data is fresh
  // enough and we don't want to duplicate work.
  useEffect(() => {
    reload({ reason: "initial" });
    if (!isAccountant) {
      axios.get(`${API}/users`)
        .then(r => setUsers(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
        .catch(() => {});
    }
    const reconId = setInterval(() => {
      const sinceLast = Date.now() - lastReloadStartedRef.current;
      if (sinceLast < 5 * 60_000) return; // SSE already kept us fresh
      reload({ reason: "reconcile" });
    }, 10 * 60_000); // every 10 minutes
    // Separate 60s tick to re-render the "x ago" label (no API call).
    const labelId = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => { clearInterval(reconId); clearInterval(labelId); };
  }, []); // eslint-disable-line

  function fmtAgo(ts) {
    if (!ts) return "never";
    const s = Math.floor((nowTick - ts) / 1000);
    if (s < 10) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  }

  async function doComplete(r) {
    try {
      await axios.patch(`${API}/accounts/reminders/${r.id}`, { status: "completed" });
      toast.success("Payment marked as completed");
      setConfirmComplete(null);
      reload({ reason: "post-mutation" });
    } catch { toast.error("Could not update reminder"); }
  }
  async function doReopen(r, reason) {
    try {
      const resp = await axios.post(`${API}/accounts/reminders/${r.id}/reopen`, { reason });
      toast.success(`Reopened — now ${resp.data.new_status}`);
      setReopenOf(null);
      reload({ reason: "post-mutation" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
  }
  async function doAck(r) {
    try {
      await axios.post(`${API}/accounts/reminders/${r.id}/acknowledge`);
      toast.success("Acknowledged");
      reload({ reason: "post-mutation" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not acknowledge"); }
  }

  const filtered = useMemo(() => {
    switch (filter) {
      case "overdue":    return items.filter(x => x.status === "overdue");
      case "due_today":  return items.filter(x => x.status === "due");
      case "upcoming":   return items.filter(x => x.status === "upcoming");
      case "completed":  return items.filter(x => x.status === "completed");
      case "attention":  return items.filter(x => x.status === "overdue" || x.status === "due");
      case "all_active": return items.filter(x => x.status !== "completed" && x.status !== "cancelled");
      case "all_records":
      default:           return items;
    }
  }, [filter, items]);

  const emptyMessage = {
    overdue:     { title: "No overdue payments",    hint: "You're all caught up." },
    due_today:   { title: "Nothing due today",       hint: "Enjoy the breather." },
    upcoming:    { title: "No upcoming payments",    hint: "Nothing scheduled yet." },
    completed:   { title: "Nothing completed yet",   hint: "Confirm a reminder as completed to see it here." },
    attention:   { title: "Nothing needs attention", hint: "Overdue & due-today reminders will appear here." },
    all_active:  { title: "No active reminders",     hint: "All payments are up to date." },
    all_records: { title: "No records yet",          hint: "Create your first reminder with the button above." },
  }[filter];

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="accounts-reminders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
          <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Payment Desk</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 text-[11px] text-[#8A8F98]"
               data-testid="rem-last-updated"
               title={lastUpdated ? new Date(lastUpdated).toLocaleString() : ""}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${
              lastUpdated && (Date.now() - lastUpdated) < 60_000
                ? "bg-[#39FF14]" : "bg-[#8A8F98]/50"}`} />
            <span>Last updated {fmtAgo(lastUpdated)}</span>
          </div>
          <Button variant="outline" size="sm"
                  onClick={() => reload({ reason: "manual" })}
                  disabled={refreshing || inflightRef.current}
                  data-testid="rem-refresh-btn"
                  className="h-9 bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#1A1F1C] hover:text-[#39FF14]">
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin text-[#39FF14]" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="rem-create-btn"
                  className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
            <Plus size={14} className="mr-1.5" /> New Payment Reminder
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Overdue" tone="red" icon={AlertTriangle}
                     count={summary?.overdue?.count || 0}
                     amount={summary?.overdue?.amount || 0}
                     active={filter === "overdue"}
                     onClick={() => setFilter(filter === "overdue" ? "attention" : "overdue")}
                     testid="card-overdue" />
        <SummaryCard label="Due Today" tone="amber" icon={Clock3}
                     count={summary?.due_today?.count || 0}
                     amount={summary?.due_today?.amount || 0}
                     active={filter === "due_today"}
                     onClick={() => setFilter(filter === "due_today" ? "attention" : "due_today")}
                     testid="card-due-today" />
        <SummaryCard label="Upcoming" tone="muted" icon={CalendarDays}
                     count={summary?.upcoming?.count || 0}
                     amount={summary?.upcoming?.amount || 0}
                     active={filter === "upcoming"}
                     onClick={() => setFilter(filter === "upcoming" ? "attention" : "upcoming")}
                     testid="card-upcoming" />
        <SummaryCard label="Completed · This Month" tone="green" icon={CheckCircle2}
                     count={summary?.completed_this_month?.count || 0}
                     amount={summary?.completed_this_month?.amount || 0}
                     active={filter === "completed"}
                     onClick={() => setFilter(filter === "completed" ? "attention" : "completed")}
                     testid="card-completed" />
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {[
          { k: "attention",   label: "Requires attention" },
          { k: "all_active",  label: "All active" },
          { k: "overdue",     label: "Overdue" },
          { k: "due_today",   label: "Due today" },
          { k: "upcoming",    label: "Upcoming" },
          { k: "completed",   label: "Completed" },
          { k: "all_records", label: "All records" },
        ].map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`filter-${t.k}`}
                  className={`text-[11px] font-heading uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all
                              ${filter === t.k
                                ? "border-[#39FF14] bg-[#0f1a12] text-[#39FF14]"
                                : "border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={emptyMessage.title} hint={emptyMessage.hint} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="rem-card-grid">
          {filtered.map(r => (
            <ReminderCard key={r.id} r={r}
                          onComplete={(rem) => setConfirmComplete(rem)}
                          onPay={(x) => setPayOpen(x)}
                          onReopen={(rem) => setReopenOf(rem)}
                          canReopen={canReopen}
                          onAck={doAck} canAck={canAck}
                          onChat={(rem) => setChatOf(rem)} />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <details className="rounded-xl border border-[#3A4640] bg-[#121815] mt-6" data-testid="rem-details-table-wrap">
          <summary className="cursor-pointer px-4 py-3 text-[11px] font-heading uppercase tracking-wider text-[#8A8F98] flex items-center gap-2">
            <ChevronRight size={14} /> Show detailed records
          </summary>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f1512] text-[#8A8F98]">
                <tr>
                  <th className="text-left px-3 py-2">Title</th>
                  <th>Due</th><th>Amount</th><th>Priority</th>
                  <th>Pref. method</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t border-[#3A4640]" data-testid={`rem-row-${r.id}`}>
                    <td className="px-3 py-2">{r.display_title || r.title || "Payment reminder"}</td>
                    <td className="text-center text-xs">{r.due_date}{r.due_time ? ` · ${r.due_time}` : ""}</td>
                    <td className="text-right">{r.amount ? fmtInr(r.amount) : <span className="text-[#8A8F98]">—</span>}</td>
                    <td className="text-center"><PriorityBadge value={r.priority} /></td>
                    <td className="text-center text-xs text-[#8EC2A9]">{r.preferred_payment_method || "—"}</td>
                    <td className="text-center">
                      <Badge variant={r.status === "overdue" ? "destructive" : r.status === "completed" ? "default" : "secondary"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="text-right pr-2">
                      {r.status !== "completed" && r.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" onClick={() => setConfirmComplete(r)}
                                data-testid={`rem-row-complete-${r.id}`}>
                          <CheckCircle2 size={14} />
                        </Button>
                      )}
                      {(r.status === "completed" || r.status === "cancelled") && canReopen && (
                        <Button size="sm" variant="ghost" onClick={() => setReopenOf(r)}
                                className="text-[#FFB46B] hover:text-[#F8F9FA]"
                                data-testid={`rem-row-reopen-${r.id}`}>
                          <RotateCcw size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#0B0F0C] border-[#3A4640]" data-testid="rem-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wallet size={16} className="text-[#39FF14]" /> New Payment Reminder
            </DialogTitle>
          </DialogHeader>
          <ReminderForm isAccountant={isAccountant} users={users}
                        onDone={() => { setOpen(false); reload(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent className="max-w-xl bg-[#0B0F0C] border-[#3A4640]" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard size={16} className="text-[#39FF14]" /> Record Payment
            </DialogTitle>
          </DialogHeader>
          {payOpen && (
            <PaymentForm initialReminder={payOpen}
                         onDone={() => { setPayOpen(null); reload(); }} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmCompleteDialog
        open={!!confirmComplete}
        reminder={confirmComplete}
        onCancel={() => setConfirmComplete(null)}
        onConfirm={doComplete}
      />

      <ReopenDialog
        open={!!reopenOf}
        reminder={reopenOf}
        onCancel={() => setReopenOf(null)}
        onConfirm={doReopen}
      />

      <ReminderChatDrawer
        open={!!chatOf}
        reminder={chatOf}
        onClose={() => setChatOf(null)}
      />
    </div>
  );
}

// ===================================================================
// Payment form
// ===================================================================
function PaymentForm({ onDone, initialReminder = null }) {
  const { user } = useAuth();
  const [desc, setDesc] = useState(initialReminder?.display_title || initialReminder?.title || "");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payTime, setPayTime] = useState("");
  const [payeeId, setPayeeId] = useState(initialReminder?.payee_id || null);
  const [payeeName, setPayeeName] = useState("");
  const [amount, setAmount] = useState(initialReminder?.amount || null);
  const [method, setMethod] = useState(initialReminder?.preferred_payment_method || "UPI");
  const [bank, setBank] = useState(initialReminder?.preferred_bank_account_id || null);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [notifyOther, setNotifyOther] = useState(false);
  const [staged, setStaged] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const todayIso = new Date().toISOString().slice(0, 10);
  const isFuture = payDate > todayIso;

  async function submit() {
    if (!method) { toast.error("Please select the actual payment method"); return; }
    if (!desc.trim() && !payeeId && !amount && !initialReminder?.id) {
      toast.error("Add at least a description, payee or amount");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/accounts/payments`, {
        description: desc.trim() || null,
        payment_date: payDate,
        payment_time: payTime || null,
        payee_id: payeeId,
        amount, payment_mode: method,
        bank_account_id: bank, reference: reference.trim() || null,
        notes: notes.trim() || null,
        reminder_id: initialReminder?.id || null,
        notify_other: notifyOther,
      });
      const pid = r.data.id;
      for (const f of staged) {
        const fd = new FormData(); fd.append("file", f);
        try {
          await axios.post(`${API}/accounts/attachments/payment/${pid}`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.error(`Could not upload ${f.name}`); }
      }
      setConfirmation({ payment: r.data, notified: notifyOther });
      try { await axios.post(`${API}/accounts/_ping-created/payment/${pid}`); } catch { /* best-effort */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record payment");
    } finally {
      setSaving(false);
    }
  }

  if (confirmation) {
    const p = confirmation.payment;
    const isScheduled = p.status === "scheduled";
    return (
      <div className="space-y-3" data-testid="pay-confirmation">
        <div className={`rounded-xl border p-5 text-center ${
          isScheduled
            ? "border-[#FFB46B]/40 bg-gradient-to-br from-[#2a1a0a] to-[#121815]"
            : "border-[#39FF14]/40 bg-gradient-to-br from-[#0f2214] to-[#121815]"
        }`}>
          <CheckCircle2 size={36} className={`mx-auto mb-2 ${isScheduled ? "text-[#FFB46B]" : "text-[#39FF14]"}`} />
          <div className={`text-[10px] font-heading uppercase tracking-[0.18em] ${isScheduled ? "text-[#FFB46B]" : "text-[#39FF14]"}`}
               data-testid="pay-confirmation-status">
            {isScheduled ? "Payment scheduled" : "Payment completed"}
          </div>
          <div className="text-xl font-heading font-bold text-[#F8F9FA] mt-1">{fmtInr(p.amount) || "—"}</div>
          <div className="text-xs text-[#8A8F98] mt-0.5">{amountInWords(p.amount)}</div>
          {isScheduled && (
            <div className="text-[11px] text-[#FFB46B] mt-2">
              Scheduled for {p.payment_date}. The reminder will stay open until you confirm payment on that date.
            </div>
          )}
        </div>
        <div className="rounded-lg border border-[#3A4640] bg-[#121815] p-4 text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-[#8A8F98]">Description</span><span className="text-[#F8F9FA]">{p.description}</span></div>
          <div className="flex justify-between"><span className="text-[#8A8F98]">Date</span><span className="text-[#F8F9FA]">{p.payment_date}</span></div>
          <div className="flex justify-between"><span className="text-[#8A8F98]">Method</span><span className="text-[#F8F9FA]">{p.payment_mode}</span></div>
          {p.reference && <div className="flex justify-between"><span className="text-[#8A8F98]">Reference</span><span className="text-[#F8F9FA] font-mono text-xs">{p.reference}</span></div>}
          {confirmation.notified && (
            <div className="flex justify-between pt-1 border-t border-[#3A4640] mt-2">
              <span className="text-[#8A8F98]">Notified</span>
              <span className="text-[#39FF14]">{user?.role === "accountant" ? "Owner" : "Accountant"}</span>
            </div>
          )}
        </div>
        <Button onClick={onDone} data-testid="pay-confirmation-done"
                className="w-full bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
          Done
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="payment-form">
      {initialReminder && (
        <div className="rounded-lg border border-[#39FF14]/30 bg-[#0f2214]/50 p-3 text-xs text-[#8EC2A9]">
          Recording payment for reminder: <span className="font-medium text-[#F8F9FA]">{initialReminder.display_title || initialReminder.title}</span>
        </div>
      )}
      {isFuture && (
        <div className="rounded-lg border border-[#FFB46B]/40 bg-[#2a1a0a]/70 p-3 text-xs text-[#FFB46B] flex items-start gap-2"
             data-testid="pay-future-banner">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>This payment is scheduled for the future.</b> It will be saved with
            status <span className="font-semibold">SCHEDULED</span> and will not mark the linked reminder as completed.
            Use <b>Mark as Paid</b> on the date it actually happens.
          </div>
        </div>
      )}
      <SectionCard title="Description / Reference (optional)" icon={FileText} testid="pay-sec-desc">
        <Input placeholder='e.g. "Power Weeder — supplier payment" (optional)'
               value={desc} onChange={e => setDesc(e.target.value)} data-testid="pay-desc" />
      </SectionCard>

      <SectionCard title="Amount" icon={IndianRupee} testid="pay-sec-amount">
        <AmountInput value={amount} onChange={setAmount} large />
      </SectionCard>

      <SectionCard title="When was it paid?" icon={CalendarDays} testid="pay-sec-when">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                 className="bg-[#0f1512]" data-testid="pay-date" />
          <Input type="time" value={payTime} onChange={e => setPayTime(e.target.value)}
                 className="bg-[#0f1512]" data-testid="pay-time" />
        </div>
      </SectionCard>

      <SectionCard title="Payee" icon={UsersIcon} testid="pay-sec-payee">
        <PayeePicker value={payeeId} name={payeeName}
                     onChange={(id, nm) => { setPayeeId(id); setPayeeName(nm || ""); }} />
      </SectionCard>

      <SectionCard title="Actual payment method" icon={CreditCard} testid="pay-sec-method"
                   right={<span className="text-[10px] text-[#8A8F98] italic hidden sm:inline">This is what you actually used</span>}>
        <PaymentMethodGrid value={method} onChange={setMethod} testidPrefix="pay-actual-method" />
      </SectionCard>

      <SectionCard title="Bank account used (optional)" icon={Building2} testid="pay-sec-bank">
        <BankPicker value={bank} onChange={setBank} testid="pay-bank" />
      </SectionCard>

      <SectionCard title="Reference / transaction number" icon={Receipt} testid="pay-sec-ref">
        <Input placeholder="e.g. UPI txn, cheque number, bank ref"
               value={reference} onChange={e => setReference(e.target.value)}
               className="font-mono bg-[#0f1512]" data-testid="pay-ref" />
      </SectionCard>

      <SectionCard title="Documents" icon={Paperclip} testid="pay-sec-attach">
        <AttachmentPanel staged={staged} setStaged={setStaged} />
      </SectionCard>

      <SectionCard title="Notes" icon={FileText} testid="pay-sec-notes">
        <Textarea placeholder="Any additional context"
                  value={notes} onChange={e => setNotes(e.target.value)}
                  data-testid="pay-notes" className="bg-[#0f1512] min-h-[60px]" />
      </SectionCard>

      <label className="flex items-center gap-2 text-sm text-[#F8F9FA] px-1">
        <input type="checkbox" checked={notifyOther} onChange={e => setNotifyOther(e.target.checked)}
               data-testid="pay-notify" />
        Notify {user?.role === "accountant" ? "Owner" : "Accountant"}
      </label>

      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-[#0B0F0C]/95 backdrop-blur border-t border-[#3A4640] flex justify-end gap-2">
        <Button onClick={submit} disabled={saving} data-testid="pay-submit"
                className={`font-heading font-semibold ${isFuture ? "bg-[#FFB46B] text-[#0B0F0C] hover:bg-[#FFB46B]/90" : "bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"}`}>
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
          {isFuture ? "Schedule Payment" : "Record Payment"}
        </Button>
      </div>
    </div>
  );
}

// ===================================================================
// Payments listing page
// ===================================================================
export function AccountsPayments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  async function reload() {
    try { const r = await axios.get(`${API}/accounts/payments`); setItems(r.data.items || []); }
    catch { toast.error("Could not load payments"); }
  }
  useEffect(() => { reload(); }, []);
  async function markPaid(id) {
    try {
      await axios.post(`${API}/accounts/payments/${id}/mark-paid`);
      toast.success("Payment confirmed as paid");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
  }
  return (
    <div className="p-6 space-y-4" data-testid="accounts-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
          <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Payments</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pay-record-btn"
                className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
          <Plus size={14} className="mr-1.5" /> Record Payment
        </Button>
      </div>
      {items.length === 0 ? (
        <EmptyState title="No payments yet" hint="Click Record Payment to log your first entry." icon={Receipt} />
      ) : (
        <div className="rounded-xl border border-[#3A4640] overflow-hidden bg-[#121815]">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1512] text-[#8A8F98]">
              <tr>
                <th className="text-left px-3 py-2">Description</th>
                <th>Payment Date</th><th>Amount</th><th>Method</th><th>Ref</th><th>Status</th><th>Recorded</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-t border-[#3A4640]" data-testid={`pay-row-${p.id}`}>
                  <td className="px-3 py-2">{p.description || <span className="text-[#8A8F98]">—</span>}</td>
                  <td className="text-center text-xs">{p.payment_date}{p.payment_time ? ` · ${p.payment_time}` : ""}</td>
                  <td className="text-right">{p.amount ? fmtInr(p.amount) : <span className="text-[#8A8F98]">—</span>}</td>
                  <td className="text-center text-xs text-[#8EC2A9]">{p.payment_mode}</td>
                  <td className="text-center text-xs font-mono text-[#8A8F98]">{p.reference || "—"}</td>
                  <td className="text-center" data-testid={`pay-row-status-${p.id}`}>
                    {p.status === "scheduled" ? (
                      <Badge className="bg-[#3a2619] text-[#FFB46B] border-[#D97706]/40 border">SCHEDULED</Badge>
                    ) : (
                      <Badge className="bg-[#0f2214] text-[#39FF14] border-[#39FF14]/40 border">COMPLETED</Badge>
                    )}
                  </td>
                  <td className="text-center text-[11px] text-[#8A8F98]"
                      title={`Recorded by ${p.created_by_email}`}>
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-right pr-2">
                    {p.status === "scheduled" && (
                      <Button size="sm" variant="outline"
                              className="h-7 px-2 border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14] hover:text-[#39FF14]"
                              onClick={() => markPaid(p.id)}
                              data-testid={`pay-row-markpaid-${p.id}`}>
                        Mark as Paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-[#0B0F0C] border-[#3A4640]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard size={16} className="text-[#39FF14]" /> Record Payment
            </DialogTitle>
          </DialogHeader>
          <PaymentForm onDone={() => { setOpen(false); reload(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================================================================
// Payees page
// ===================================================================
export function AccountsPayees() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  async function reload() {
    try { const r = await axios.get(`${API}/accounts/payees`); setItems(r.data.items || []); }
    catch { toast.error("Could not load payees"); }
  }
  useEffect(() => { reload(); }, []);
  async function add() {
    if (!name.trim()) return;
    try { await axios.post(`${API}/accounts/payees`, { name: name.trim() }); setName(""); reload(); }
    catch (e) { toast.error(e.response?.status === 409 ? "Payee already exists" : "Error"); }
  }
  return (
    <div className="p-6 space-y-4" data-testid="accounts-payees-page">
      <div>
        <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
        <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Payees</h1>
      </div>
      <div className="flex gap-2 max-w-md">
        <Input placeholder="New payee name" value={name} onChange={e => setName(e.target.value)}
               data-testid="payee-new-name" className="bg-[#0f1512]" />
        <Button onClick={add} data-testid="payee-add-btn"
                className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">Add</Button>
      </div>
      {items.length === 0 ? (
        <EmptyState title="No payees yet" hint="Add your first payee above." icon={UsersIcon} />
      ) : (
        <div className="rounded-xl border border-[#3A4640] overflow-hidden bg-[#121815]">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1512] text-[#8A8F98]"><tr><th className="text-left px-3 py-2">Name</th><th>Category</th><th>Phone</th><th>Usage</th></tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-t border-[#3A4640]" data-testid={`payee-row-${p.id}`}>
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="text-center">{p.category || "—"}</td>
                  <td className="text-center">{p.phone || "—"}</td>
                  <td className="text-center">{p.usage_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Bank Accounts page
// ===================================================================
export function AccountsBanks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [f, setF] = useState({ bank_name: "", nickname: "", account_number: "", ifsc: "", branch: "" });
  const { user } = useAuth();
  // Owner, admin, co_admin AND accountant may manage bank accounts (per V3 policy).
  const canCreate = ["admin", "co_admin", "owner", "accountant"].includes(user?.role);
  async function reload() {
    try {
      const r = await axios.get(`${API}/accounts/bank-accounts`,
        { params: { active_only: !showInactive } });
      setItems(r.data.items || []);
    }
    catch { toast.error("Could not load bank accounts"); }
  }
  useEffect(() => { reload(); }, [showInactive]); // eslint-disable-line
  async function submit() {
    try {
      await axios.post(`${API}/accounts/bank-accounts`, f);
      setOpen(false);
      setF({ bank_name: "", nickname: "", account_number: "", ifsc: "", branch: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  }
  async function toggleActive(b) {
    try {
      await axios.post(`${API}/accounts/bank-accounts/${b.id}/${b.is_active ? "deactivate" : "activate"}`);
      toast.success(b.is_active ? "Bank account deactivated" : "Bank account re-activated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  }
  return (
    <div className="p-6 space-y-4" data-testid="accounts-banks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
          <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Bank Accounts</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-[#8A8F98]">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                   data-testid="bank-show-inactive" />
            Show inactive
          </label>
          {canCreate && (
            <Button onClick={() => setOpen(true)} data-testid="bank-add-btn"
                    className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">
              <Plus size={14} className="mr-1.5" /> Add Account
            </Button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <EmptyState title="No bank accounts yet" hint="Add your first bank account above." icon={Landmark} />
      ) : (
        <div className="rounded-xl border border-[#3A4640] overflow-hidden bg-[#121815]">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1512] text-[#8A8F98]"><tr><th className="text-left px-3 py-2">Bank</th><th>Nickname</th><th>Masked No.</th><th>IFSC</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className={`border-t border-[#3A4640] ${!b.is_active ? "opacity-60" : ""}`}
                    data-testid={`bank-row-${b.id}`}>
                  <td className="px-3 py-2">{b.bank_name}</td>
                  <td className="text-center">{b.nickname}</td>
                  <td className="text-center font-mono text-[#8EC2A9]">{b.account_number_masked}</td>
                  <td className="text-center text-xs">{b.ifsc || "—"}</td>
                  <td className="text-center">
                    {b.is_active
                      ? <Badge className="bg-[#0f2214] text-[#39FF14] border-[#39FF14]/40 border">ACTIVE</Badge>
                      : <Badge className="bg-[#1a2320] text-[#8A8F98] border-[#3A4640] border">INACTIVE</Badge>}
                  </td>
                  <td className="text-right pr-2">
                    {canCreate && (
                      <Button size="sm" variant="ghost"
                              className="h-7 px-2 text-[#8A8F98] hover:text-[#F8F9FA]"
                              onClick={() => toggleActive(b)}
                              data-testid={`bank-toggle-${b.id}`}>
                        {b.is_active ? "Deactivate" : "Reactivate"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#0B0F0C] border-[#3A4640]">
          <DialogHeader><DialogTitle className="font-heading">Add Bank Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Bank name (e.g. HDFC)" value={f.bank_name}
                   onChange={e => setF({ ...f, bank_name: e.target.value })} data-testid="bank-name" />
            <Input placeholder="Nickname (e.g. HDFC Main Current)" value={f.nickname}
                   onChange={e => setF({ ...f, nickname: e.target.value })} data-testid="bank-nick" />
            <Input placeholder="Account number" value={f.account_number}
                   onChange={e => setF({ ...f, account_number: e.target.value })} data-testid="bank-no" />
            <Input placeholder="IFSC (optional)" value={f.ifsc}
                   onChange={e => setF({ ...f, ifsc: e.target.value })} data-testid="bank-ifsc" />
            <Input placeholder="Branch (optional)" value={f.branch}
                   onChange={e => setF({ ...f, branch: e.target.value })} data-testid="bank-branch" />
            <Button onClick={submit} data-testid="bank-submit"
                    className="w-full bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90 font-heading font-semibold">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================================================================
// Activity feed — V2 human-readable timeline
// ===================================================================
const ACTIVITY_LABELS = {
  "reminder:created":              { icon: Plus,           label: "Reminder Created",        group: "reminders" },
  "reminder:updated":              { icon: Edit3,          label: "Reminder Updated",        group: "reminders" },
  "reminder:completed":            { icon: CheckCircle2,   label: "Reminder Completed",      group: "reminders" },
  "reminder:completed_via_payment":{ icon: CheckCircle2,   label: "Reminder Completed",      group: "reminders" },
  "reminder:reopened":             { icon: RotateCcw,      label: "Reminder Reopened",       group: "reminders" },
  "reminder:reopened_via_payment": { icon: RotateCcw,      label: "Reminder Reopened",       group: "reminders" },
  "reminder:acknowledged":         { icon: Eye,            label: "Reminder Acknowledged",   group: "reminders" },
  "reminder:cancelled":            { icon: X,              label: "Reminder Cancelled",      group: "reminders" },
  "reminder:payment_linked":       { icon: CreditCard,     label: "Payment Linked",          group: "reminders" },
  "reminder:payment_scheduled":    { icon: Clock3,         label: "Payment Scheduled",       group: "reminders" },
  "payment:created_completed":     { icon: CreditCard,     label: "Payment Recorded",        group: "payments" },
  "payment:created_scheduled":     { icon: Clock3,         label: "Payment Scheduled",       group: "payments" },
  "payment:confirmed_paid":        { icon: CheckCircle2,   label: "Payment Confirmed",       group: "payments" },
  "payment:reopened":              { icon: RotateCcw,      label: "Payment Reopened",        group: "payments" },
  "bank_account:created":          { icon: Landmark,       label: "Bank Account Added",      group: "banks" },
  "bank_account:updated":          { icon: Landmark,       label: "Bank Account Updated",    group: "banks" },
  "bank_account:deactivated":      { icon: Landmark,       label: "Bank Account Deactivated",group: "banks" },
  "bank_account:activated":        { icon: Landmark,       label: "Bank Account Reactivated",group: "banks" },
  "payee:created":                 { icon: UsersIcon,      label: "Payee Added",             group: "banks" },
  "chat:message_sent":             { icon: MessageSquare,  label: "Message Sent",            group: "chat" },
};

function activityMeta(a) {
  const key = `${a.entity_type}:${a.action}`;
  return ACTIVITY_LABELS[key] || { icon: FileText, label: a.action, group: "other" };
}

function friendlyActor(email, role) {
  if (!email) return "System";
  // Hide test-ish identifiers from regular users: show just the local part
  const name = email.split("@")[0];
  const roleTag = role === "owner" ? "Owner" :
                  role === "accountant" ? "Accountant" :
                  role === "admin" ? "Admin" :
                  role === "co_admin" ? "Co-Admin" : null;
  return roleTag ? `${roleTag} (${name})` : name;
}

function groupByDay(items) {
  const groups = {};
  for (const a of items) {
    const d = new Date(a.created_at);
    const key = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    (groups[key] ||= []).push(a);
  }
  return groups;
}

export function AccountsActivity() {
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounts/activity`, { params: { limit: 200 } });
      setItems(r.data.items || []);
    } catch { toast.error("Could not load activity"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  useRealtime((ev) => {
    if (ev.type === "reminder" || ev.type === "payment" || ev.type === "chat") load();
  }, { enabled: true });

  const filtered = useMemo(() => items.filter((a) => {
    const m = activityMeta(a);
    if (group !== "all" && m.group !== group) return false;
    if (q) {
      const hay = `${m.label} ${a.actor_email || ""} ${JSON.stringify(a.details || {})}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [items, group, q]);

  const grouped = useMemo(() => groupByDay(filtered), [filtered]);

  return (
    <div className="p-6 space-y-4 max-w-[1100px]" data-testid="accounts-activity-page">
      <div>
        <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
        <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Activity</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { k: "all",       label: "All" },
          { k: "payments",  label: "Payments" },
          { k: "reminders", label: "Reminders" },
          { k: "banks",     label: "Banks & Payees" },
          { k: "chat",      label: "Messages" },
          { k: "other",     label: "Other" },
        ].map((t) => (
          <button key={t.k} onClick={() => setGroup(t.k)} data-testid={`activity-filter-${t.k}`}
                  className={`text-[11px] font-heading uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all
                              ${group === t.k
                                ? "border-[#39FF14] bg-[#0f1a12] text-[#39FF14]"
                                : "border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA]"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search activity…" className="pl-9 bg-[#0f1512]"
                 data-testid="activity-search" />
        </div>
      </div>

      {loading && <div className="text-center text-xs text-[#8A8F98] py-6">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <EmptyState title="No activity yet" hint="Payment Desk events will appear here as a timeline." icon={Clock3} />
      )}

      <div className="space-y-6">
        {Object.entries(grouped).map(([day, rows]) => (
          <div key={day}>
            <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98] mb-2">
              {day}
            </div>
            <div className="relative pl-6 space-y-3">
              <div className="absolute left-2 top-1 bottom-1 w-px bg-[#3A4640]" />
              {rows.map((a) => {
                const m = activityMeta(a);
                const Icon = m.icon;
                const amount = a.details?.amount;
                const d = new Date(a.created_at);
                const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={a.id} className="relative"
                       data-testid={`activity-row-${a.id}`}>
                    <div className="absolute -left-[22px] top-1 w-6 h-6 rounded-full bg-[#121815] border border-[#3A4640]
                                    flex items-center justify-center">
                      <Icon size={11} className="text-[#39FF14]" />
                    </div>
                    <div className="rounded-lg border border-[#3A4640] bg-[#121815] px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[#F8F9FA]">{m.label}</div>
                          <div className="text-[11px] text-[#8A8F98] mt-0.5 flex flex-wrap items-center gap-x-3">
                            <span>{timeStr}</span>
                            <span>· By {friendlyActor(a.actor_email, a.actor_role)}</span>
                            {amount != null && <span>· {fmtInr(amount, { hidePaise: true })}</span>}
                            {a.details?.reason && <span className="italic">· "{a.details.reason}"</span>}
                            {a.details?.new_status && <span>· now <b className="uppercase text-[#39FF14]">{a.details.new_status}</b></span>}
                            {a.details?.preview && <span>· "{a.details.preview}"</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===================================================================
// Recurring reminders page
// ===================================================================
export function AccountsRecurring() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [occurrencesBySeries, setOccurrencesBySeries] = useState({});
  const [payeesById, setPayeesById] = useState({});
  const [usersById, setUsersById] = useState({});
  const [banksById, setBanksById] = useState({});
  const [attachmentsByRem, setAttachmentsByRem] = useState({});
  const [loading, setLoading] = useState(true);
  const [detailOf, setDetailOf] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const [remResp, payees, users, banks] = await Promise.all([
        axios.get(`${API}/accounts/reminders`, { params: { limit: 500, include_cancelled: true } }),
        axios.get(`${API}/accounts/payees`, { params: { limit: 1000 } }).catch(() => ({ data: { items: [] } })),
        (["admin","co_admin","owner"].includes(user?.role)
          ? axios.get(`${API}/users`)
          : Promise.resolve({ data: [] })),
        axios.get(`${API}/accounts/bank-accounts`).catch(() => ({ data: { items: [] } })),
      ]);
      const all = remResp.data.items || [];
      const parents = all.filter(x => x.recurrence && !x.is_recurring_occurrence);
      setItems(parents);
      // Build occurrences lookup
      const byS = {};
      for (const x of all) {
        if (x.is_recurring_occurrence && x.recurrence_series_id) {
          (byS[x.recurrence_series_id] = byS[x.recurrence_series_id] || []).push(x);
        }
      }
      Object.values(byS).forEach(arr => arr.sort((a, b) => (b.due_date || "").localeCompare(a.due_date || "")));
      setOccurrencesBySeries(byS);
      // Payees / users / banks lookups
      const pMap = {}; (payees.data.items || []).forEach(p => { pMap[p.id] = p; });
      setPayeesById(pMap);
      const uList = Array.isArray(users.data) ? users.data : (users.data?.items || []);
      const uMap = {}; uList.forEach(u => { uMap[u.id] = u; });
      setUsersById(uMap);
      const bMap = {}; (banks.data.items || []).forEach(b => { bMap[b.id] = b; });
      setBanksById(bMap);
    } catch { toast.error("Could not load Recurring Reminders"); }
    finally { setLoading(false); }
  }

  useEffect(() => { reload(); }, []); // eslint-disable-line

  // Fetch attachments for the opened reminder detail (optional data)
  useEffect(() => {
    if (!detailOf) return;
    if (attachmentsByRem[detailOf.id] !== undefined) return;
    axios.get(`${API}/accounts/attachments/reminder/${detailOf.id}`)
      .then(r => setAttachmentsByRem(prev => ({ ...prev, [detailOf.id]: r.data.items || [] })))
      .catch(() => setAttachmentsByRem(prev => ({ ...prev, [detailOf.id]: [] })));
  }, [detailOf]); // eslint-disable-line

  return (
    <div className="p-6 space-y-5" data-testid="accounts-recurring-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
          <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5">Recurring Reminders</h1>
          <div className="text-xs text-[#8A8F98] mt-1">
            Scheduled obligations that regenerate automatically. Click a row for full detail.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}
                data-testid="rec-refresh-btn"
                className="h-9 bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#1A1F1C] hover:text-[#39FF14]">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin text-[#39FF14]" : ""}`} />
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {loading && items.length === 0 ? (
        <div className="rounded-xl border border-[#3A4640] bg-[#121815] p-8 text-center text-sm text-[#8A8F98]">
          Loading recurring reminders…
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="No recurring reminders yet"
                    hint="Create a reminder with Repeat every month to see it here."
                    icon={Repeat} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rec-grid">
          {items.map(r => (
            <RecurringCard key={r.id} r={r}
                           payee={r.payee_id ? payeesById[r.payee_id] : null}
                           assignee={r.assigned_to_user_id ? usersById[r.assigned_to_user_id] : null}
                           bank={r.preferred_bank_account_id ? banksById[r.preferred_bank_account_id] : null}
                           onOpen={() => setDetailOf(r)} />
          ))}
        </div>
      )}

      <RecurringDetailDrawer
        open={!!detailOf}
        onClose={() => setDetailOf(null)}
        r={detailOf}
        payee={detailOf?.payee_id ? payeesById[detailOf.payee_id] : null}
        assignee={detailOf?.assigned_to_user_id ? usersById[detailOf.assigned_to_user_id] : null}
        bank={detailOf?.preferred_bank_account_id ? banksById[detailOf.preferred_bank_account_id] : null}
        createdBy={detailOf?.created_by_id ? usersById[detailOf.created_by_id] : null}
        occurrences={detailOf ? (occurrencesBySeries[detailOf.recurrence_series_id] || []) : []}
        attachments={detailOf ? (attachmentsByRem[detailOf.id] || []) : []}
      />
    </div>
  );
}

// Compute the next due date for a monthly recurrence based on day_of_month.
// Uses the parent reminder's last_occurrence_date (if present) as the anchor,
// otherwise today. Clamps day_of_month to the target month's length.
function nextDueDateFor(r) {
  if (!r?.recurrence) return null;
  if (r.recurrence.active === false) return null;
  const day = Math.min(31, Math.max(1, Number(r.recurrence.day_of_month) || 1));
  const base = r.last_occurrence_date ? new Date(r.last_occurrence_date + "T00:00:00") : new Date();
  // Start from base, roll to the first future occurrence of `day`.
  const now = new Date();
  let candidate = new Date(base.getFullYear(), base.getMonth(), day);
  if (candidate <= now) {
    // next month
    candidate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const lastDay = new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0).getDate();
    candidate.setDate(Math.min(day, lastDay));
  } else {
    const lastDay = new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0).getDate();
    candidate.setDate(Math.min(day, lastDay));
  }
  return candidate.toISOString().slice(0, 10);
}

function fmtINRLight(n) {
  if (n == null || n === "") return null;
  try { return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n)); }
  catch { return String(n); }
}

function methodLabel(m) {
  if (!m) return null;
  const map = { bank_transfer: "Bank Transfer", upi: "UPI", cash: "Cash",
                cheque: "Cheque", card: "Card", other: "Other" };
  return map[m] || m;
}

function RecurringCard({ r, payee, assignee, bank, onOpen }) {
  const active = r.recurrence?.active !== false;
  const nextDue = nextDueDateFor(r);
  const amount = r.amount != null ? fmtINRLight(r.amount) : null;
  const title = r.title || payee?.name || "Recurring payment";
  const method = methodLabel(r.preferred_payment_method);
  return (
    <button type="button" onClick={onOpen}
            data-testid={`rec-card-${r.id}`}
            className="group text-left rounded-xl border border-[#3A4640] bg-[#121815] px-4 py-3.5 hover:border-[#39FF14]/50 hover:bg-[#141b17] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <Repeat size={14} className="shrink-0 text-[#39FF14]" />
            <div className="font-heading font-semibold text-[#F8F9FA] truncate" data-testid={`rec-title-${r.id}`}>
              {title}
            </div>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-[#8A8F98]">
            {amount && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#0f2214] text-[#39FF14] font-heading font-semibold">
                <IndianRupee size={10} /> {amount}
              </span>
            )}
            {payee?.name && <span className="truncate">· {payee.name}</span>}
          </div>
        </div>
        <span className={`shrink-0 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
          active
            ? "border-[#39FF14]/40 bg-[#0f2214] text-[#39FF14]"
            : "border-[#8A8F98]/30 bg-[#1A1F1C] text-[#8A8F98]"}`}
              data-testid={`rec-status-${r.id}`}>
          {active ? "Active" : "Paused"}
        </span>
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div className="flex items-center gap-1 text-[#8A8F98]">
          <CalendarDays size={11} className="text-[#8EC2A9]" />
          Monthly · day {r.recurrence?.day_of_month}
          {r.due_time && <span className="text-[#8EC2A9]"> · {r.due_time}</span>}
        </div>
        <div className="flex items-center gap-1 text-[#F8F9FA] font-medium"
             data-testid={`rec-nextdue-${r.id}`}>
          <Clock3 size={11} className="text-[#FFB020]" />
          Next due: {nextDue
            ? new Date(nextDue).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </div>
        {method && (
          <div className="flex items-center gap-1 text-[#8A8F98]">
            <CreditCard size={11} className="text-[#8EC2A9]" /> {method}
            {bank?.bank_name && <span className="text-[#8EC2A9]"> · {bank.bank_name}</span>}
          </div>
        )}
        {assignee && (
          <div className="flex items-center gap-1 text-[#8A8F98] truncate">
            <UsersIcon size={11} className="text-[#8EC2A9]" />
            <span className="truncate">{assignee.name || assignee.email?.split("@")[0]}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function RecurringDetailDrawer({ open, onClose, r, payee, assignee, bank, createdBy, occurrences, attachments }) {
  if (!r) return null;
  const active = r.recurrence?.active !== false;
  const nextDue = nextDueDateFor(r);
  const amountFmt = r.amount != null ? fmtINRLight(r.amount) : null;
  const amountWords = r.amount ? amountToWordsIN(r.amount) : null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B0F0C] border-[#3A4640] p-0 overflow-hidden"
                     data-testid="rec-detail-drawer">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#3A4640]">
          <DialogTitle className="font-heading flex items-center gap-2 text-lg">
            <Repeat size={16} className="text-[#39FF14]" />
            <span className="truncate">{r.title || payee?.name || "Recurring payment"}</span>
          </DialogTitle>
          <div className="mt-2 flex items-center gap-2 text-[11px]">
            <span className={`uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              active
                ? "border-[#39FF14]/40 bg-[#0f2214] text-[#39FF14]"
                : "border-[#8A8F98]/30 bg-[#1A1F1C] text-[#8A8F98]"}`}>
              {active ? "Active" : "Paused"}
            </span>
            <span className="text-[#8A8F98]">Monthly · day {r.recurrence?.day_of_month}</span>
          </div>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-5">
          <RecSection title="Summary">
            <RecRow label="Payee" value={payee?.name || "—"} testid="rec-detail-payee" />
            <RecRow label="Amount"
                    value={amountFmt ? <span className="text-[#39FF14] font-semibold">₹ {amountFmt}</span> : "—"}
                    testid="rec-detail-amount" />
            {amountWords && (
              <RecRow label="In words"
                      value={<span className="text-[11px] text-[#8EC2A9] italic">{amountWords}</span>} />
            )}
            <RecRow label="Priority" value={(r.priority || "medium").toUpperCase()} />
          </RecSection>

          <RecSection title="Schedule">
            <RecRow label="Frequency" value="Monthly" />
            <RecRow label="Pay day" value={`Day ${r.recurrence?.day_of_month}`} />
            <RecRow label="Preferred time" value={r.due_time || "—"} />
            <RecRow label="Next occurrence"
                    value={nextDue
                      ? <span className="text-[#FFB020] font-medium">
                          {new Date(nextDue).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
                        </span>
                      : "—"}
                    testid="rec-detail-nextdue" />
            <RecRow label="Last generated"
                    value={r.last_occurrence_date
                      ? new Date(r.last_occurrence_date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
                      : "—"} />
          </RecSection>

          <RecSection title="Payment">
            <RecRow label="Preferred method"
                    value={methodLabel(r.preferred_payment_method) || "—"} />
            <RecRow label="Preferred bank"
                    value={bank ? `${bank.bank_name || "Bank"}${bank.account_number_masked ? ` · ${bank.account_number_masked}` : ""}` : "—"} />
          </RecSection>

          <RecSection title="Assignment">
            <RecRow label="Assigned to"
                    value={assignee ? `${assignee.name || assignee.email?.split("@")[0]}${assignee.role ? ` · ${assignee.role}` : ""}` : "—"} />
          </RecSection>

          {(r.instructions || r.notes) && (
            <RecSection title="Instructions & Notes">
              {r.instructions && (
                <div className="rounded-lg bg-[#121815] border border-[#3A4640] px-3 py-2 text-sm text-[#F8F9FA] whitespace-pre-wrap break-words">
                  {r.instructions}
                </div>
              )}
              {r.notes && (
                <div className="rounded-lg bg-[#121815] border border-[#3A4640] px-3 py-2 text-xs text-[#8EC2A9] whitespace-pre-wrap break-words">
                  Notes: {r.notes}
                </div>
              )}
            </RecSection>
          )}

          {attachments && attachments.length > 0 && (
            <RecSection title={`Documents (${attachments.length})`}>
              <ul className="space-y-1">
                {attachments.map(a => (
                  <li key={a.id} className="flex items-center gap-2 text-xs text-[#F8F9FA]">
                    <Paperclip size={12} className="text-[#39FF14]" />
                    <span className="truncate">{a.filename || a.name || "attachment"}</span>
                  </li>
                ))}
              </ul>
            </RecSection>
          )}

          <RecSection title={`Recent Occurrences${occurrences?.length ? ` (${occurrences.length})` : ""}`}>
            {!occurrences || occurrences.length === 0 ? (
              <div className="text-xs text-[#8A8F98]">No past occurrences yet.</div>
            ) : (
              <ul className="space-y-1" data-testid="rec-detail-history">
                {occurrences.slice(0, 8).map(o => (
                  <li key={o.id} className="flex items-center justify-between text-xs">
                    <span className="text-[#F8F9FA]">
                      {o.due_date
                        ? new Date(o.due_date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
                        : "—"}
                    </span>
                    <span className={`uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded-full border ${
                      o.status === "completed"
                        ? "border-[#39FF14]/40 bg-[#0f2214] text-[#39FF14]"
                        : o.status === "overdue"
                        ? "border-[#FF4D4F]/40 bg-[#2a1414] text-[#FF4D4F]"
                        : "border-[#8A8F98]/30 bg-[#1A1F1C] text-[#8A8F98]"}`}>
                      {o.status || "upcoming"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </RecSection>

          <RecSection title="Audit">
            <RecRow label="Created by"
                    value={createdBy ? (createdBy.name || createdBy.email?.split("@")[0]) : (r.created_by_email?.split("@")[0] || "—")} />
            <RecRow label="Created at"
                    value={r.created_at
                      ? new Date(r.created_at).toLocaleString()
                      : "—"} />
            <RecRow label="Last updated"
                    value={r.updated_at
                      ? new Date(r.updated_at).toLocaleString()
                      : "—"} />
          </RecSection>
        </div>
        <div className="border-t border-[#3A4640] px-5 py-3 flex items-center justify-between bg-[#121815]">
          <div className="text-[10px] text-[#8A8F98]">
            Viewing this reminder never alters payment status.
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="rec-detail-close"
                  className="bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#1A1F1C]">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecSection({ title, children }) {
  return (
    <section>
      <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8F98] font-heading mb-1.5">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function RecRow({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm" data-testid={testid}>
      <div className="text-[#8A8F98] shrink-0">{label}</div>
      <div className="text-[#F8F9FA] text-right min-w-0 break-words">{value}</div>
    </div>
  );
}

// Lightweight amount-to-Indian-words helper for the detail drawer only.
// Covers 0 – 999,99,99,999 with lakh/crore grouping.
function amountToWordsIN(n) {
  const num = Number(n);
  if (!isFinite(num) || num < 0) return null;
  const a = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
             "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen",
             "eighteen", "nineteen"];
  const b = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
  function below100(x) {
    if (x < 20) return a[x];
    return b[Math.floor(x / 10)] + (x % 10 ? "-" + a[x % 10] : "");
  }
  function below1000(x) {
    const h = Math.floor(x / 100), rest = x % 100;
    const s = [];
    if (h) s.push(a[h] + " hundred");
    if (rest) s.push(below100(rest));
    return s.join(" ");
  }
  let x = Math.floor(num);
  const parts = [];
  const crore = Math.floor(x / 10000000); x %= 10000000;
  const lakh = Math.floor(x / 100000); x %= 100000;
  const thousand = Math.floor(x / 1000); x %= 1000;
  if (crore) parts.push(below1000(crore) + " crore");
  if (lakh) parts.push(below100(lakh) + " lakh");
  if (thousand) parts.push(below100(thousand) + " thousand");
  if (x) parts.push(below1000(x));
  const whole = parts.length ? parts.join(" ") : "zero";
  const paise = Math.round((num - Math.floor(num)) * 100);
  const paiseStr = paise ? ` and ${below100(paise)} paise` : "";
  return `Rupees ${whole}${paiseStr} only`.replace(/\s+/g, " ").trim();
}


// ===================================================================
// Global Owner ↔ Accountant chat — standalone page
// ===================================================================
export function AccountsGlobalChat() {
  const { user } = useAuth();
  const role = user?.role;
  const allowed = ["owner", "accountant", "admin", "co_admin"].includes(role);

  if (!allowed) {
    return (
      <div className="p-6 max-w-[900px]" data-testid="global-chat-forbidden">
        <h1 className="text-2xl font-heading font-bold text-[#F8F9FA]">Accounts Communication</h1>
        <div className="mt-4 rounded-xl border border-[#3A4640] bg-[#121815] p-6 text-center text-sm text-[#8A8F98]">
          Your role doesn't have access to this chat.
        </div>
      </div>
    );
  }

  const subtitle = role === "accountant"
    ? "Direct line to the Owner / Management. Messages here are not tied to a specific payment reminder."
    : "Direct line to the Accountant. Messages here are not tied to a specific payment reminder.";

  return (
    <div className="p-6 flex flex-col max-w-[1100px] h-[calc(100vh-48px)]"
         data-testid="accounts-global-chat-page">
      <div className="shrink-0">
        <div className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#8A8F98]">Accounts</div>
        <h1 className="text-2xl font-heading font-bold text-[#F8F9FA] mt-0.5 flex items-center gap-2">
          <MessageSquare size={20} className="text-[#39FF14]" />
          Accounts Communication
        </h1>
        <div className="text-xs text-[#8A8F98] mt-1">{subtitle}</div>
      </div>
      <div className="mt-4 flex-1 min-h-0 rounded-xl border border-[#3A4640] bg-[#0B0F0C] overflow-hidden flex flex-col">
        <ChatPanel scope="global" scopeId="main"
                   heightClass="h-full" />
      </div>
      <div className="shrink-0 text-[11px] text-[#8A8F98] mt-2">
        Chat and attachments never change payment or reminder status. Attachment limits: PDF/JPG/PNG only, 10 MB max.
      </div>
    </div>
  );
}
